import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AppLayout from './components/AppLayout';
import LandingPage from './components/LandingPage';
import HouseholdSetup from './components/HouseholdSetup';
import HomeScreen from './components/HomeScreen';
import SwipeScreen from './components/SwipeScreen';
import WeeklyPlanner from './components/planner/WeeklyPlanner';
import TonightView from './components/TonightView';
import StatsScreen from './components/StatsScreen';
import FamilyScreen from './components/FamilyScreen';
import SettingsScreen from './components/SettingsScreen';
import RecipeCreate from './components/RecipeCreate';
import UpgradeScreen from './components/UpgradeScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import DeleteAccountInfo from './components/DeleteAccountInfo';
import ResetPassword from './components/ResetPassword';
import UpdatePassword from './components/UpdatePassword';
import ErrorFallback from './components/ErrorFallback';

function AppRoutes() {
  const { user, loading, membership } = useAuth();
  const { code } = useParams();

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="font-body text-ink-soft">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  if (!membership) {
    return <HouseholdSetup initialCode={code} />;
  }

  if (code) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<HomeScreen />} />
        <Route path="swipe" element={<SwipeScreen />} />
        {/* Legacy /plan retired — the board is the plan now. Redirect old links. */}
        <Route path="plan" element={<Navigate to="/planner" replace />} />
        <Route path="planner" element={<WeeklyPlanner />} />
        <Route path="tonight" element={<TonightView />} />
        <Route path="stats" element={<StatsScreen />} />
        <Route path="recipes/new" element={<RecipeCreate />} />
        <Route path="family" element={<FamilyScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="upgrade" element={<UpgradeScreen />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfService />} />
            <Route path="delete-account" element={<DeleteAccountInfo />} />
            {/* Public: reachable regardless of auth/household state. The reset
                link creates a recovery session, so /update-password must render
                outside the app shell. */}
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="update-password" element={<UpdatePassword />} />
            <Route path="join/:code" element={<AppRoutes />} />
            <Route path="*" element={<AppRoutes />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
